import React, { useState } from 'react';
import FormInput from 'components/FormInput';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import { Footer } from '../Footer';
import { useNavigate, useParams } from 'react-router-dom';
import {
  OtpVerificationModal,
  OtpDetails,
} from 'components/common/OtpVerificationModal';
import { VerificationSchema } from './schema';
import { useDispatch, useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import toast from 'react-hot-toast';
import { useUpdateUserDetailsMutation } from 'redux/services/auth';
import { Loader } from 'components/common/Loader';
import { getHeaders } from 'helper/helper';
import { saveUserDetails } from 'redux/authSlice';
import Tick from '../../../../assets/images/greencircle.png';
import './VerificationForm.scss';
import { useTranslation } from 'react-i18next';

export const VerificationForm: React.FC<{}> = ({ children }) => {
  const { t } = useTranslation();
  const { page } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [modal, setModal] = useState<{
    type: 'email' | 'mobile';
    value: string;
  } | null>(null);
  const [updateUserDetails, { isLoading, isError }] =
    useUpdateUserDetailsMutation();
  const user = useSelector((state: RootStates) => state.auth.userDetails);
  const formik = useFormik({
    initialValues: {
      email: user.email,
      mobile: user.mobile || '',
      emailVerified: user.onboardingData?.isEmailVerified ?? false,
      mobileVerified: user.onboardingData?.isMobileVerified ?? false,
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: VerificationSchema,
    onSubmit: async (values) => {
      if (values.emailVerified && values.mobileVerified) {
        const headers = await getHeaders();
        const payload: any = await updateUserDetails({
          headers: headers,
          body: {
            mobile: values.mobile || user.mobile,
            email: values.email || user.email,
            onboardingData: {
              ...user.onboardingData,
              mobile: values.mobile || user.mobile,
              email: values.email || user.email,
              isEmailVerified: true,
              isMobileVerified: true,
              lastPageFilled: page,
            },
          },
        });

        if (!isError) {
          dispatch(saveUserDetails(payload.data));
          navigate(`/onboarding/${Number(page) + 1}`);
        } else {
          toast.error('Update failed, please try again later');
        }
      } else {
        toast.error(
          `Please verify your ${
            !values.emailVerified ? 'Email' : 'Mobile number'
          }`,
        );
      }
    },
  });

  const handleHide = () => setModal(null);
  const getSetModalHandler =
    (type: 'email' | 'mobile', value: string) => async () => {
      if (
        formik.getFieldMeta(type).error ||
        !String(formik.getFieldMeta(type).value).length
      ) {
        return toast.error(t('fill_in_correct_value', { type }));
      }
      setModal({ type, value });
    };
  const handleSubmitOtpVerification = (otpDetails: OtpDetails) => {
    if (otpDetails.mode === 'email') {
      formik.setFieldValue('emailVerified', otpDetails.verified);
    } else {
      formik.setFieldValue('mobileVerified', otpDetails.verified);
    }
    setModal(null);
  };

  const { errors, touched, values } = formik;

  return (
    <>
      <h5>{t('onboarding_header2')}</h5>
      <Form onSubmit={formik.handleSubmit}>
        <div className="form-content">
          <div className="verifcation-ctrls d-flex">
            <FormInput
              label={t('email')}
              type="email"
              name="email"
              disabled={values.emailVerified}
              value={values.email}
              touched={touched.email}
              error={errors.email}
              onChange={formik.handleChange}
            />
            {values.emailVerified ? (
              <div className="entity-verified">
                <img alt="" src={Tick} />
                <span> {`(${t('verified')})`}</span>
              </div>
            ) : (
              <Button
                className="mb-3"
                onClick={getSetModalHandler('email', values.email)}
              >
                {`${t('btn_label_otp_verify')} ->`}
              </Button>
            )}
          </div>
          <div className="verifcation-ctrls d-flex">
            <FormInput
              label={t('mobile')}
              type="number"
              name="mobile"
              disabled={values.mobileVerified}
              value={values.mobile}
              touched={touched.mobile}
              error={errors.mobile}
              onChange={formik.handleChange}
            />
            {formik.values.mobileVerified ? (
              <div className="entity-verified">
                <img alt="" src={Tick} />
                <span> {`(${t('verified')})`}</span>
              </div>
            ) : (
              <Button
                className="mb-3"
                onClick={getSetModalHandler('mobile', values.mobile)}
              >
                {`${t('btn_label_otp_verify')} ->`}
              </Button>
            )}
          </div>
        </div>
        {!!modal && (
          <OtpVerificationModal
            open={!!modal}
            value={modal?.value ?? ''}
            type={modal?.type ?? 'email'}
            onHide={handleHide}
            onSubmit={handleSubmitOtpVerification}
          />
        )}

        <Footer page={Number(page!)}>{children}</Footer>
      </Form>
      {isLoading && <Loader />}
    </>
  );
};
